import { formatDate as asdFormatDate } from "@a-safe-digital/format-date"
import { Controller } from "@hotwired/stimulus"

import { resolveLocale } from "@shared/lib/date-locale-map"

/**
 * Connects to data-controller="contacts--format-date"
 *
 * Formats a Created/Updated timestamp CLIENT-SIDE so the Hotwire V2 Site detail
 * Information tab matches the React page byte-for-byte (CONEK-7132).
 *
 * The React page (company-information-content.tsx) renders these dates in the
 * BROWSER via `@a-safe-digital/format-date`'s `formatDate` — date-fns short "P"
 * format (locale-aware numeric, e.g. "10/03/2026"), in the browser's timezone.
 * The server-side fallback (`l(..., :long)`) differed in BOTH format ("10 de
 * marzo de 2026") and timezone (UTC), so a record created between 00:00 UTC and
 * the user's local midnight showed a different DAY on V2 than on React.
 *
 * We call the SAME `@a-safe-digital/format-date` `formatDate` with the SAME
 * default "P" format, resolving the date-fns locale from
 * `document.documentElement.lang` (the same server-set lang React's i18n seeds
 * from) via the shared `resolveLocale`. Deliberately NOT importing
 * `@shared/lib/format-date`, which would pull react-i18next + the app locales
 * into the Hotwire bundle — this stays lean while producing identical output.
 * The <time> element's server-rendered text content is the no-JS fallback,
 * overwritten here once JS runs.
 */
export default class extends Controller {
  static values = { iso: String }

  connect() {
    if (!this.hasIsoValue || !this.isoValue) return

    const lang = document.documentElement.lang || navigator?.language || "en-GB"
    try {
      const formatted = asdFormatDate(this.isoValue, {
        locale: resolveLocale(lang),
      })
      if (formatted) this.element.textContent = formatted
    } catch (error) {
      // Leave the server-rendered fallback in place, but surface the cause so a
      // latent locale-resolution error isn't permanently invisible.
      console.warn("contacts--format-date: could not format date", error)
    }
  }
}
